import { useEffect, useEffectEvent, useRef } from "react";
import type { EffectCallback } from "react";
import type { ActorRef, EventObject, Snapshot } from "xstate";

export const useMountEffect = (effect: EffectCallback): void => {
  const runEffect = useEffectEvent(effect);

  useEffect(() => runEffect(), []);
};

type EmittedActorEvent<TEmitted extends EventObject, TType extends TEmitted["type"]> = TEmitted &
  (TType extends "*" ? unknown : { type: TType });

/**
 * Subscribes once to an event emitted by a component-owned actor. The actor
 * and event type belong to the mount lifetime; the listener always comes from
 * the latest committed render.
 */
export const useMountActorEvent = <
  TSnapshot extends Snapshot<unknown>,
  TEvent extends EventObject,
  TEmitted extends EventObject,
  TType extends TEmitted["type"],
>(
  actor: ActorRef<TSnapshot, TEvent, TEmitted>,
  type: TType,
  listener: (event: EmittedActorEvent<TEmitted, TType>) => void,
): void => {
  const sourceRef = useRef({ actor, type });
  const handleEvent = useEffectEvent(listener);

  useEffect(() => {
    const source = sourceRef.current;
    const subscription = source.actor.on(source.type, (event) => {
      handleEvent(event);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);
};
